import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * HeroShader — Domain-warped fractional Brownian motion (fBM) noise,
 * rendered via Three.js on a fullscreen plane behind the hero text.
 * Adds mouse-tracked violet glow, vignette and film grain.
 */
const vertexShader = /* glsl */ `
void main() {
  gl_Position = vec4(position, 1.0);
}
`;

const fragmentShader = /* glsl */ `
precision highp float;

uniform float u_time;
uniform vec2  u_resolution;
uniform vec2  u_mouse;
uniform vec3  u_bg;
uniform vec3  u_accent;
uniform vec3  u_mid;

// 1. Base hash
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

// Value noise — bilinear interpolation of 4 hashed corners
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

// 2. fBM = 5 octaves
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p *= 2.02;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float aspect = u_resolution.x / u_resolution.y;

  // Aspect-corrected sampling space
  vec2 p = uv;
  p.x *= aspect;
  p *= 2.1;

  float t = u_time * 0.09;

  // 3. Domain warping — first pass deforms second pass
  vec2 q = vec2(
    fbm(p + vec2(0.0, t)),
    fbm(p + vec2(5.2, -t) + 7.3)
  );

  vec2 r = vec2(
    fbm(p + 1.7 * q + vec2(1.7, 9.2) + 0.15 * t),
    fbm(p + 1.7 * q + vec2(8.3, 2.8) - 0.13 * t)
  );

  float n = fbm(p + 1.4 * r + t);

  // Mouse glow (smooth-followed) — tight halo
  vec2 mUv = u_mouse / u_resolution;
  vec2 a = uv;       a.x *= aspect;
  vec2 b = mUv;      b.x *= aspect;
  float dist = distance(a, b);
  float glow = smoothstep(0.22, 0.0, dist);

  // 4. Color mix — bg → mid → accent driven by warped noise (denser smoke)
  vec3 col = mix(u_bg, u_mid, smoothstep(0.10, 0.55, n));
  col      = mix(col, u_accent, smoothstep(0.40, 0.92, n + glow * 0.18));

  // Add a soft accent halo around the cursor — tighter, less intense
  col += u_accent * glow * 0.18 * smoothstep(0.30, 1.0, n);

  // Vignette
  vec2 vUv = uv - 0.5;
  float vign = 1.0 - dot(vUv, vUv) * 1.15;
  col *= clamp(vign, 0.55, 1.0);

  // Film grain
  float grain = (hash(uv * u_resolution.xy + fract(u_time * 60.0)) - 0.5) * 0.045;
  col += grain;

  // Crush blacks for cinematic feel
  col = max(col - 0.012, vec3(0.0));

  gl_FragColor = vec4(col, 1.0);
}
`;

export default function HeroShader() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));

    const scene = new THREE.Scene();
    const camera = new THREE.Camera();

    const material = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        u_time: { value: 0 },
        u_resolution: { value: new THREE.Vector2(1, 1) },
        u_mouse: { value: new THREE.Vector2(0, 0) },
        u_bg: { value: new THREE.Color(0x050505) },
        u_mid: { value: new THREE.Color("#2a1f3d") },
        u_accent: { value: new THREE.Color("#b794f6") },
      },
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent ? parent.clientWidth : window.innerWidth;
      const h = parent ? parent.clientHeight : window.innerHeight;
      renderer.setSize(w, h, false);
      material.uniforms.u_resolution.value.set(w, h);
      // Initialize mouse to center on first paint
      material.uniforms.u_mouse.value.set(w * 0.5, h * 0.5);
    };
    resize();
    window.addEventListener("resize", resize);

    let targetX = 0;
    let targetY = 0;
    let curX = 0;
    let curY = 0;
    let initialised = false;

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      // Only register inside hero rectangle, but still smooth toward center otherwise
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (inside) {
        targetX = e.clientX - rect.left;
        targetY = rect.height - (e.clientY - rect.top);
        if (!initialised) {
          curX = targetX;
          curY = targetY;
          initialised = true;
        }
      }
    };
    window.addEventListener("mousemove", onMove);

    const start = performance.now();
    let raf = 0;
    let visible = true;

    const onVis = () => {
      visible = !document.hidden;
      if (visible) raf = requestAnimationFrame(tick);
    };
    document.addEventListener("visibilitychange", onVis);

    const tick = (now) => {
      curX += (targetX - curX) * 0.07;
      curY += (targetY - curY) * 0.07;
      material.uniforms.u_mouse.value.set(curX, curY);
      material.uniforms.u_time.value = (now - start) * 0.001;
      renderer.render(scene, camera);
      if (visible) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("visibilitychange", onVis);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="hero-shader-canvas"
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ display: "block" }}
    />
  );
}
